export const LIVE_STAGES = ['needs_decision', 'task_created', 'task_in_progress']
export const ALL_STAGES = ['awaiting_review', 'needs_decision', 'task_created', 'task_in_progress', 'resolved', 'dismissed']
export const SEVERITIES = ['critical', 'high', 'medium', 'low']
export const SOURCES = ['guru', 'incident', 'task_form', 'manual', 'imported']
export const DEFAULT_SORT = 'severity_desc'
export const PAGE_SIZE = 25
export const SECTION_SIZE = 5
export const DASHBOARD = 'dashboard'

export const VIEWS = [
  { key: 'mine', label: 'My remedials', countKey: 'mine', stages: LIVE_STAGES, extra: { 'filter[mine]': 1 }, hint: 'Open remedials you own, or where you are assigned to one of their tasks.', empty: 'Nothing is waiting on you. Remedials you own, or tasks you are assigned to, will show here.' },
  { key: 'all_open', label: 'All open', countKey: 'allOpen', stages: LIVE_STAGES, extra: {}, hint: 'Every remedial that still needs putting right.', empty: 'No open remedials. Add one when you find something that needs putting right.' },
  { key: 'overdue', label: 'Overdue', countKey: 'overdue', stages: LIVE_STAGES, extra: { 'filter[overdue]': 1 }, danger: true, hint: 'Open remedials whose due date has passed.', empty: 'Nothing is overdue. Well done.' },
  { key: 'due_this_week', label: 'Due this week', countKey: 'dueThisWeek', stages: LIVE_STAGES, extra: { 'filter[due_this_week]': 1 }, hint: 'Open remedials due today or in the next 7 days.', empty: 'Nothing is due in the next 7 days.' },
  { key: 'needs_decision', label: 'Needs a decision', countKey: 'needsDecision', stages: ['needs_decision'], extra: {}, hint: 'Nobody has created a task for these or completed them yet.', empty: 'Every remedial has a decision. Nothing is waiting for a task or completion.' },
  { key: 'task_in_progress', label: 'Task in progress', countKey: 'taskInProgress', stages: ['task_created', 'task_in_progress'], extra: {}, hint: 'A task has been created to put these right.', empty: 'No remedials have tasks under way.' },
  { key: 'awaiting_review', label: 'Awaiting review', countKey: 'awaitingReview', stages: ['awaiting_review'], extra: {}, hint: 'Found by our AI in your documents and waiting for someone to check them.', empty: 'Nothing is waiting for review.' },
  { key: 'resolved', label: 'Resolved', countKey: 'resolved', stages: ['resolved'], extra: {}, hint: 'Put right, by completing their tasks or by marking them complete.', empty: 'No remedials have been resolved yet.' },
  { key: 'dismissed', label: 'Dismissed', countKey: 'dismissed', stages: ['dismissed'], extra: {}, hint: 'Closed without any work, with a reason recorded.', empty: 'No remedials have been dismissed.' },
]

export const VIEW_KEYS = VIEWS.map(view => view.key)

export const viewDef = key => VIEWS.find(view => view.key === key) || VIEWS[1]

export const SORT_COLUMNS = {
  severity: ['severity_desc', 'severity_asc'],
  title: ['title_asc', 'title_desc'],
  source: ['source_asc', 'source_desc'],
  due: ['due_asc', 'due_desc'],
  owner: ['owner_asc', 'owner_desc'],
  stage: ['stage_asc', 'stage_desc'],
  created: ['created_desc', 'created_asc'],
}

export const EXTRA_SORTS = ['last_comment_desc', 'last_comment_asc']

export const SORT_KEYS = [...Object.values(SORT_COLUMNS).flat(), ...EXTRA_SORTS]

export const COMMENT_FILTERS = ['with', 'without']

export const SORT_OPTIONS = [
  { value: 'severity_desc', label: 'Severity, highest first' },
  { value: 'severity_asc', label: 'Severity, lowest first' },
  { value: 'due_asc', label: 'Due date, soonest first' },
  { value: 'due_desc', label: 'Due date, latest first' },
  { value: 'title_asc', label: 'Title, A to Z' },
  { value: 'title_desc', label: 'Title, Z to A' },
  { value: 'owner_asc', label: 'Owner, A to Z' },
  { value: 'owner_desc', label: 'Owner, Z to A' },
  { value: 'source_asc', label: 'Source, A to Z' },
  { value: 'source_desc', label: 'Source, Z to A' },
  { value: 'stage_asc', label: 'Stage, needs a decision first' },
  { value: 'stage_desc', label: 'Stage, closed first' },
  { value: 'created_desc', label: 'Newest first' },
  { value: 'created_asc', label: 'Oldest first' },
  { value: 'last_comment_desc', label: 'Last comment, newest first' },
  { value: 'last_comment_asc', label: 'Last comment, oldest first' },
]

export const DASHBOARD_SECTIONS = [
  { key: 'overdue', title: 'Overdue', view: 'overdue', sort: 'due_asc', seeAll: { view: 'overdue' }, empty: 'Nothing is overdue. Well done.' },
  { key: 'due_this_week', title: 'Due this week', view: 'due_this_week', sort: 'due_asc', seeAll: { view: 'due_this_week' }, empty: 'Nothing is due in the next 7 days.' },
  { key: 'critical_no_task', title: 'Critical or high with no task yet', view: 'needs_decision', severity: ['critical', 'high'], sort: 'severity_desc', seeAll: { view: 'needs_decision', severity: ['critical', 'high'] }, empty: 'Every critical and high remedial already has a task or a decision.' },
]

export const EMPTY_FILTERS = {
  q: '',
  severity: [],
  source: [],
  stage: [],
  owner: null,
  dueFrom: null,
  dueTo: null,
  incident: null,
  bp: null,
  portfolio: null,
  estate: null,
  involves: null,
  involvesName: null,
  resolvedFrom: null,
  resolvedTo: null,
  raisedFrom: null,
  raisedTo: null,
  comments: null,
}

export const VIEW_RESET = { q: null, severity: null, source: null, stage: null, owner: null, dueFrom: null, dueTo: null, incident: null, resolvedFrom: null, resolvedTo: null, raisedFrom: null, raisedTo: null, comments: null }

export const FILTER_RESET = { ...VIEW_RESET, involves: null, involvesName: null }

const LEGACY_STATUS_VIEW = {
  open: 'all_open',
  pending_review: 'awaiting_review',
  converted: 'task_in_progress',
  resolved: 'resolved',
  dismissed: 'dismissed',
}

const splitList = value => (value ? String(value).split(',').map(part => part.trim()).filter(Boolean) : [])

const isDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')

const isUuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value || '')

const dateParam = (searchParams, key) => (isDate(searchParams.get(key)) ? searchParams.get(key) : null)

export const readView = searchParams => {
  const view = searchParams.get('view')
  if (view === DASHBOARD || VIEW_KEYS.includes(view)) return view
  if (searchParams.get('overdue') === '1') return 'overdue'
  const legacy = LEGACY_STATUS_VIEW[searchParams.get('status')]
  if (legacy) return legacy
  if (dateParam(searchParams, 'resolvedFrom') || dateParam(searchParams, 'resolvedTo')) return 'resolved'
  const narrowing = ['severity', 'incident', 'q', 'stage', 'owner', 'involves', 'comments'].some(key => searchParams.get(key))
  const source = searchParams.get('source')
  if (narrowing || (source && source !== 'all')) return 'all_open'
  return DASHBOARD
}

export const readFilters = searchParams => ({
  q: searchParams.get('q') || '',
  severity: splitList(searchParams.get('severity')).filter(value => SEVERITIES.includes(value)),
  source: splitList(searchParams.get('source')).filter(value => SOURCES.includes(value)),
  stage: splitList(searchParams.get('stage')).filter(value => ALL_STAGES.includes(value)),
  owner: searchParams.get('owner') || null,
  dueFrom: dateParam(searchParams, 'dueFrom'),
  dueTo: dateParam(searchParams, 'dueTo'),
  incident: searchParams.get('incident') || null,
  bp: searchParams.get('bp') || null,
  portfolio: searchParams.get('portfolio') || null,
  estate: searchParams.get('estate') || null,
  involves: isUuid(searchParams.get('involves')) ? searchParams.get('involves') : null,
  involvesName: isUuid(searchParams.get('involves')) ? (searchParams.get('involvesName') || '').trim() || null : null,
  resolvedFrom: dateParam(searchParams, 'resolvedFrom'),
  resolvedTo: dateParam(searchParams, 'resolvedTo'),
  raisedFrom: dateParam(searchParams, 'raisedFrom'),
  raisedTo: dateParam(searchParams, 'raisedTo'),
  comments: COMMENT_FILTERS.includes(searchParams.get('comments')) ? searchParams.get('comments') : null,
})

export const readSort = searchParams => {
  const sort = searchParams.get('sort')
  if (sort === 'stage') return 'stage_asc'
  return SORT_KEYS.includes(sort) ? sort : DEFAULT_SORT
}

export const withParams = (searchParams, changes) => {
  const next = new URLSearchParams(searchParams)
  Object.entries(changes).forEach(([key, raw]) => {
    const value = Array.isArray(raw) ? raw.join(',') : raw
    if (value === null || value === undefined || value === '') next.delete(key)
    else next.set(key, String(value))
  })
  return next
}

export const normaliseLegacyParams = searchParams => {
  const legacy = searchParams.has('status') || searchParams.has('overdue') || searchParams.get('source') === 'all'
  if (!legacy) return null
  const next = new URLSearchParams(searchParams)
  if (!next.has('view')) next.set('view', readView(searchParams))
  next.delete('status')
  next.delete('overdue')
  if (next.get('source') === 'all') next.delete('source')
  return next
}

export const stageOptionsForView = view => viewDef(view).stages

export const stagesFor = (view, chosen = []) => {
  const allowed = viewDef(view).stages
  const picked = chosen.filter(stage => allowed.includes(stage))
  return picked.length ? picked : allowed
}

export const scopeListParams = (scope, filters) => {
  const params = {}
  if (scope.kind === 'building') params['filter[building_passport_id]'] = scope.id
  else if (filters.bp) params['filter[building_passport_id]'] = filters.bp
  if (scope.kind === 'portfolio') params['filter[portfolio_id]'] = scope.id
  else if (scope.kind === 'all' && filters.portfolio) params['filter[portfolio_id]'] = filters.portfolio
  if (scope.kind === 'estate') params['filter[estate_id]'] = scope.id
  else if (scope.kind === 'all' && filters.estate) params['filter[estate_id]'] = filters.estate
  return params
}

export const buildListParams = ({ scope, view, filters, sort = DEFAULT_SORT, page = 1, perPage = PAGE_SIZE }) => {
  const f = { ...EMPTY_FILTERS, ...filters }
  const def = viewDef(view)
  const params = { page, per_page: perPage, sort, ...scopeListParams(scope, f), ...def.extra }
  params['filter[stage]'] = stagesFor(view, f.stage).join(',')
  const q = (f.q || '').trim()
  if (q) params['filter[search]'] = q
  if (f.severity.length) params['filter[severity]'] = f.severity.join(',')
  if (f.source.length) params['filter[source]'] = f.source.join(',')
  if (f.owner) params['filter[owner_user_id]'] = f.owner
  if (f.dueFrom) params['filter[due_from]'] = f.dueFrom
  if (f.dueTo) params['filter[due_to]'] = f.dueTo
  if (f.incident) params['filter[source_incident_id]'] = f.incident
  if (f.involves) params['filter[involves]'] = f.involves
  if (f.resolvedFrom) params['filter[resolved_from]'] = f.resolvedFrom
  if (f.resolvedTo) params['filter[resolved_to]'] = f.resolvedTo
  if (f.raisedFrom) params['filter[raised_from]'] = f.raisedFrom
  if (f.raisedTo) params['filter[raised_to]'] = f.raisedTo
  if (f.comments === 'with') params['filter[has_comments]'] = 1
  if (f.comments === 'without') params['filter[has_comments]'] = 0
  return params
}

export const buildStatsParams = (scope, filters) => {
  const f = { ...EMPTY_FILTERS, ...filters }
  const params = Object.fromEntries(
    Object.entries(scopeListParams(scope, f))
      .map(([key, value]) => [key.replace(/^filter\[(.+)\]$/, '$1'), value])
  )
  if (f.involves) params.involves = f.involves
  return params
}

export const buildSectionParams = (scope, filters, section) => buildListParams({
  scope,
  view: section.view,
  filters: { ...EMPTY_FILTERS, bp: filters.bp, portfolio: filters.portfolio, estate: filters.estate, involves: filters.involves || null, severity: section.severity || [] },
  sort: section.sort,
  page: 1,
  perPage: SECTION_SIZE,
})

export const sortState = sort => {
  if (EXTRA_SORTS.includes(sort)) return { column: null, dir: sort.endsWith('_asc') ? 'asc' : 'desc' }
  const entry = Object.entries(SORT_COLUMNS).find(([, pair]) => pair.includes(sort))
  if (!entry) return { column: 'severity', dir: 'desc' }
  return { column: entry[0], dir: sort.endsWith('_asc') ? 'asc' : 'desc' }
}

export const nextSort = (sort, column) => {
  const pair = SORT_COLUMNS[column]
  if (!pair) return sort
  return sort === pair[0] ? pair[1] : pair[0]
}

export const hasActiveFilters = filters => {
  const f = { ...EMPTY_FILTERS, ...filters }
  return Boolean(f.q || f.severity.length || f.source.length || f.stage.length || f.owner || f.dueFrom || f.dueTo || f.incident || f.involves || f.resolvedFrom || f.resolvedTo || f.raisedFrom || f.raisedTo || f.comments)
}

export const activeFilterCount = filters => {
  const f = { ...EMPTY_FILTERS, ...filters }
  return [f.severity.length > 0, f.source.length > 0, f.stage.length > 0, !!f.owner, !!f.dueFrom, !!f.dueTo, !!f.comments]
    .filter(Boolean).length
}

export const remedialsViewUrl = (target = {}, view = 'all_open', extra = {}) => {
  const params = new URLSearchParams()
  let base = '/actions'
  if (target.bpId) {
    base = `/bp-list/${target.bpId}/compliance`
    params.set('section', 'remedials')
  } else if (target.portfolioId) {
    base = `/portfolios/${target.portfolioId}`
    params.set('tab', 'actions')
  } else if (target.estateId) {
    base = `/estates/${target.estateId}`
    params.set('tab', 'actions')
  }
  if (view) params.set('view', view)
  Object.entries(extra).forEach(([key, raw]) => {
    const value = Array.isArray(raw) ? raw.join(',') : raw
    if (value !== null && value !== undefined && value !== '') params.set(key, String(value))
  })
  return `${base}?${params.toString()}`
}

export const resolvedDates = (view, range) => {
  if (view !== 'resolved' || !range) return {}
  const out = {}
  if (isDate(range.from)) out.resolvedFrom = range.from
  if (isDate(range.to)) out.resolvedTo = range.to
  return out
}

export const raisedDates = range => {
  if (!range) return {}
  const out = {}
  if (isDate(range.from)) out.raisedFrom = range.from
  if (isDate(range.to)) out.raisedTo = range.to
  return out
}

export const involvesParams = (userId, name) => {
  if (!userId) return {}
  const label = (name || '').trim()
  return label ? { involves: userId, involvesName: label } : { involves: userId }
}
